<template>
  <div class="control is-horizontal registration-codes-control">
    <div class="control-label">
      <label-name labelKey="SITE-PWA-REGISTRATION_CODES"></label-name>
    </div>

    <div class="control registration-codes-container">
      <!-- REGISTRATION CODES LIST -->
      <div v-if="items.length > 0" class="codes-list">
        <!-- HEADER LABELS -->
        <div class="codes-header-row">
          <div class="header-cell handle-cell">#</div>
          <div class="header-cell id-cell">ID</div>
          <div class="header-cell code-cell">Registrierungscode / String</div>
          <div class="header-cell actions-cell"></div>
        </div>

        <!-- CODE ROWS -->
        <div
          v-for="(item, index) in items"
          :key="'code-' + index"
          class="code-card"
        >
          <!-- 1. INDEX & REORDER HANDLE -->
          <div class="code-handle">
            <span class="tag is-rounded is-light code-index">#{{ index + 1 }}</span>
            <div class="reorder-buttons">
              <button
                type="button"
                class="button is-small is-light btn-reorder"
                :disabled="index === 0"
                @click="moveUp(index)"
                title="Nach oben verschieben"
              >
                <span class="material-symbols-outlined">keyboard_arrow_up</span>
              </button>
              <button
                type="button"
                class="button is-small is-light btn-reorder"
                :disabled="index === items.length - 1"
                @click="moveDown(index)"
                title="Nach unten verschieben"
              >
                <span class="material-symbols-outlined">keyboard_arrow_down</span>
              </button>
            </div>
          </div>

          <!-- 2. ID INPUT -->
          <div class="code-id-wrap">
            <input
              :ref="'idInput_' + index"
              type="text"
              class="input is-small code-id-input"
              v-model="item.id"
              placeholder="z.B. 1021"
              @input="emitUpdate"
              @keydown.enter.prevent="focusNextField(index, 'code')"
            />
          </div>

          <!-- 3. CODE STRING INPUT -->
          <div class="code-string-wrap">
            <input
              :ref="'codeInput_' + index"
              type="text"
              class="input is-small code-string-input"
              v-model="item.code"
              placeholder="z.B. fabasoft_mindbreeze"
              @input="emitUpdate"
              @keydown.enter.prevent="onEnterPressed(index)"
            />
          </div>

          <!-- 4. ACTION BUTTONS (DELETE) -->
          <div class="code-actions">
            <button
              type="button"
              class="button is-small is-danger is-outline is-icon-only btn-card-action btn-delete"
              title="Registrierungscode entfernen"
              @click="removeCode(index)"
            >
              <span class="material-symbols-outlined">delete</span>
            </button>
          </div>
        </div>
      </div>

      <!-- EMPTY STATE -->
      <div v-else class="empty-codes-state">
        <span class="material-symbols-outlined empty-state-icon">vpn_key</span>
        <p class="mb-2">Noch keine Registrierungscodes hinterlegt.</p>
        <button
          type="button"
          class="button is-blue is-outline is-small"
          @click="addCode"
        >
          <span class="material-symbols-outlined">add</span>
          Ersten Registrierungscode hinzufügen
        </button>
      </div>

      <!-- TOOLBAR -->
      <div v-if="items.length > 0" class="codes-toolbar">
        <button
          type="button"
          class="button is-blue is-outline is-small btn-add-code"
          @click="addCode"
        >
          <span class="material-symbols-outlined">add</span>
          Code hinzufügen
        </button>

        <span class="codes-counter tag is-light is-small ml-2">
          {{ filledItemsCount }} Code(s) hinterlegt
        </span>
      </div>

      <!-- INFO HELP TEXT -->
      <span class="help is-info-text">
        Zuordnung von Registrierungscodes (z.B. für Firmen-Rabatte oder Betriebsräte) zu Benutzer-IDs.
      </span>

      <!-- FOOTER: RAW JSON PANEL -->
      <pharmacy-raw-json-panel
        :json-data="serializeData()"
        title="JSON-Rohdaten (Registrierungscodes)"
        :rows="6"
        placeholder='{\n  "1021": "fabasoft_mindbreeze",\n  "9998": "betriebsrat_rabatt"\n}'
        @apply="onApplyRawJson"
      />
    </div>
  </div>
</template>

<script>
import PharmacyRawJsonPanel from './incl-pharmacy-raw-json-panel.vue';

export default {
  name: 'PharmacyRegistrationCodes',

  components: {
    PharmacyRawJsonPanel
  },

  props: {
    // Key-value object { "1021": "fabasoft_mindbreeze" } or JSON string
    value: {
      type: [Object, String],
      default: () => ({})
    }
  },

  data() {
    return {
      items: this.parseValue(this.value),
      isInternalUpdate: false
    };
  },

  computed: {
    filledItemsCount() {
      return this.items.filter(item => item.id && item.id.trim() !== '').length;
    }
  },

  watch: {
    value: {
      deep: true,
      handler(newVal) {
        if (this.isInternalUpdate) {
          this.isInternalUpdate = false;
          return;
        }
        this.items = this.parseValue(newVal);
      }
    }
  },

  methods: {
    parseValue(val) {
      let data = val;
      if (typeof val === 'string' && val.trim() !== '') {
        try {
          data = JSON.parse(val);
        } catch (e) {
          data = {};
        }
      }
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        data = {};
      }

      return Object.entries(data).map(([id, code]) => ({
        id: String(id),
        code: String(code || '')
      }));
    },

    serializeData() {
      const result = {};
      this.items.forEach(item => {
        const id = (item.id || '').trim();
        const code = (item.code || '').trim();
        if (id !== '') {
          result[id] = code;
        }
      });
      return result;
    },

    emitUpdate() {
      const payload = this.serializeData();
      this.isInternalUpdate = true;
      this.$emit('input', payload);
      this.$emit('change', payload);

      if (this.showRawJson) {
        this.rawJsonInput = JSON.stringify(payload, null, 2);
      }
    },

    addCode() {
      this.items.push({ id: '', code: '' });
      const newIndex = this.items.length - 1;
      this.$nextTick(() => {
        const refName = 'idInput_' + newIndex;
        if (this.$refs[refName] && this.$refs[refName][0]) {
          this.$refs[refName][0].focus();
        }
      });
      this.emitUpdate();
    },

    focusNextField(index, targetField) {
      this.$nextTick(() => {
        const refName = targetField + 'Input_' + index;
        if (this.$refs[refName] && this.$refs[refName][0]) {
          this.$refs[refName][0].focus();
        }
      });
    },

    onEnterPressed(index) {
      this.items.splice(index + 1, 0, { id: '', code: '' });
      this.$nextTick(() => {
        const refName = 'idInput_' + (index + 1);
        if (this.$refs[refName] && this.$refs[refName][0]) {
          this.$refs[refName][0].focus();
        }
      });
      this.emitUpdate();
    },

    removeCode(index) {
      this.items.splice(index, 1);
      this.emitUpdate();
    },

    moveUp(index) {
      if (index <= 0) return;
      const item = this.items.splice(index, 1)[0];
      this.items.splice(index - 1, 0, item);
      this.emitUpdate();
    },

    moveDown(index) {
      if (index >= this.items.length - 1) return;
      const item = this.items.splice(index, 1)[0];
      this.items.splice(index + 1, 0, item);
      this.emitUpdate();
    },

    onApplyRawJson(parsed, done) {
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return done('Das JSON muss ein Objekt mit Schlüssel-Wert-Paaren sein (z.B. { "1021": "code" }).');
      }
      this.items = this.parseValue(parsed);
      this.emitUpdate();
      done();
    }
  }
};
</script>

<style lang="scss" scoped>
.registration-codes-control {
  margin-top: 10px;
  margin-bottom: 20px;
  align-items: flex-start;

  > .control-label {
    padding-top: 6px;
  }
}

.registration-codes-container {
  flex: 1;
  max-width: 880px;
  width: 100%;
}

/* LIST OF CODE ROWS */
.codes-list,
.codes-toolbar,
.empty-codes-state {
  max-width: 450px;
}

.codes-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.codes-header-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  margin-bottom: 2px;
  max-width: 450px;

  .header-cell {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.3px;

    &.handle-cell {
      min-width: 46px;
      width: 46px;
      text-align: center;
    }

    &.id-cell {
      width: 75px;
      min-width: 75px;
    }

    &.code-cell {
      flex: 1;
    }

    &.actions-cell {
      width: 28px;
      min-width: 28px;
    }
  }
}

.code-card {
  display: flex;
  align-items: center;
  background-color: #fff;
  border: 1px solid #e1e6eb;
  border-radius: 6px;
  padding: 6px 10px;
  gap: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  }
}

/* HANDLE & NUMBERING */
.code-handle {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 46px;
  width: 46px;

  .code-index {
    font-size: 10px;
    font-weight: 700;
    color: #555;
    background-color: #f0f3f6;
    padding: 0 4px;
    height: 18px;
    line-height: 18px;
  }

  .reorder-buttons {
    display: flex;
    flex-direction: column;
    gap: 1px;

    .btn-reorder {
      width: 18px !important;
      height: 14px !important;
      min-width: 18px !important;
      padding: 0 !important;
      border: none !important;
      background: transparent !important;
      color: #7a7a7a !important;
      cursor: pointer;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      line-height: 1 !important;

      .material-symbols-outlined {
        margin: 0 !important;
        padding: 0 !important;
        font-size: 14px !important;
        line-height: 1 !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
      }

      &:hover:not(:disabled) {
        color: #2780e3 !important;
        background-color: #f0f4f8 !important;
        border-radius: 2px !important;
      }

      &:disabled {
        opacity: 0.3 !important;
        cursor: not-allowed;
      }
    }
  }
}

/* ID INPUT WRAP */
.code-id-wrap {
  width: 75px;
  min-width: 75px;

  .code-id-input {
    height: 30px;
    font-size: 12px;
    border-radius: 3px;
    border-color: #dbdbdb;
    padding-left: 8px;
    padding-right: 8px;
    width: 100%;
    font-family: 'Consolas', 'Courier New', monospace;
    font-weight: 600;

    &:focus {
      border-color: #45a3d2;
    }
  }
}

/* STRING INPUT WRAP */
.code-string-wrap {
  flex: 1;
  min-width: 0;

  .code-string-input {
    height: 30px;
    font-size: 12px;
    border-radius: 3px;
    border-color: #dbdbdb;
    padding-left: 8px;
    padding-right: 8px;
    width: 100%;

    &:focus {
      border-color: #45a3d2;
    }
  }
}

/* ACTION BUTTONS */
.code-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;

  .button.is-icon-only,
  .btn-card-action {
    height: 28px !important;
    width: 28px !important;
    min-width: 28px !important;
    border-radius: 4px !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 0 !important;
    line-height: 1 !important;

    .material-symbols-outlined,
    .icon,
    span {
      margin: 0 !important;
      margin-right: 0 !important;
      margin-left: 0 !important;
      padding: 0 !important;
      font-size: 15px !important;
      line-height: 1 !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      text-align: center !important;
    }

    &.btn-delete {
      color: #fff !important;
      background-color: #e74c3c !important;
      border-color: #e74c3c !important;

      &:hover {
        background-color: #c0392b !important;
        border-color: #c0392b !important;
      }
    }
  }
}

/* EMPTY STATE */
.empty-codes-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-color: #fafbfc;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  color: #64748b;
  text-align: center;
  font-size: 13px;

  .empty-state-icon {
    font-size: 32px;
    color: #94a3b8;
    margin-bottom: 6px;
  }
}

/* TOOLBAR */
.codes-toolbar {
  display: flex;
  align-items: center;
  margin-top: 10px;

  .btn-add-code {
    height: 28px;
    font-size: 12px;
    border-radius: 4px;

    .material-symbols-outlined {
      font-size: 16px;
      margin-right: 4px;
    }
  }

  .codes-counter {
    height: 24px;
    font-size: 11px;
    color: #64748b;
  }
}

.help.is-info-text {
  display: block;
  font-size: 11px;
  color: #7a7a7a;
  margin-top: 8px;
  margin-bottom: 0;
  line-height: 1.4;
}

</style>

